/*
 * Copyright (C) 2013-2026 Neckar IT GmbH, Mössingen, Germany
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Linking this library statically or dynamically with other modules is
 * making a combined work based on this library. Thus, the terms and
 * conditions of the GNU General Public License cover the whole combination.
 *
 * As a special exception, the copyright holders of this library give you
 * permission to link this library with independent modules, regardless of
 * the license terms of these independent modules, and to copy and distribute
 * the resulting combined work under terms of your choice, provided that every
 * copy of the combined work is accompanied by a complete copy of the source
 * code of this library.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
package it.neckar.runtime.context

/**
 * Immutable snapshot of the application's current runtime state.
 *
 * This aggregates the three main classification axes ([it.neckar.runtime.context.ExecutionEnvironment], [it.neckar.runtime.context.DeploymentStage], [ServiceHost])
 * along with orthogonal flags that can influence runtime behavior.
 *
 * Created once during application bootstrap and treated as read-only
 * throughout the application's lifetime.
 *
 * ## Initialization
 * * JVM: `RuntimeContext.initializeFromEnvironment(...)` reads system properties and environment variables.
 *   See `RuntimeContextSys` and `RuntimeContextEnv` for the keys that are used.
 * * JS: `RuntimeContext.initializeFromEnvironment(...)` reads the global browser variables
 *   (`window.EXECUTION_ENVIRONMENT`, `window.DEPLOYMENT_STAGE`, `window.SERVICE_HOST`) and falls back to `window.location.hostname`.
 */
data class RuntimeContext<HostType: ServiceHost>(
  /**
   * Technical execution environment — where/how the application process is running.
   * Examples: LocalDev, Docker, CI.
   */
  val executionEnvironment: ExecutionEnvironment,

  /**
   * Business / lifecycle environment — which functional stage of delivery
   * the application is in.
   * Examples: Development, Staging, Demo, Production, QA, Sandbox.
   */
  val stage: DeploymentStage,

  /**
   * Concrete target instance / domain configuration.
   * Defines hostnames, base URLs, and other connection details
   * for this specific runtime environment.
   */
  val host: HostType,

  /**
   * Indicates whether the application is currently running in a unit test context.
   * Useful to skip heavy initialization or replace integrations with mocks.
   */
  val inUnitTest: Boolean,

  /**
   * Indicates whether the application is running in debug mode
   * (e.g., started from an IDE with a debugger attached).
   * Can be used to relax timeouts or enable verbose logging.
   */
  val debugMode: Boolean,

  /**
   * If this is the initial value, this is set to true
   */
  val initialValue: Boolean,
) {

  /**
   * Returns true if the runtime context has not been initialized
   */
  fun isInitialValue(): Boolean {
    return initialValue
  }

  companion object {
    /**
     * Technical execution environment — where/how the application process is running.
     * Examples: LocalDev, Docker, CI.
     */
    val executionEnvironment: ExecutionEnvironment
      get() {
        return current.executionEnvironment
      }

    /**
     * Business / lifecycle environment — which functional stage of delivery
     * the application is in.
     * Examples: Development, Staging, Demo, Production, QA, Sandbox.
     */
    val deploymentStage: DeploymentStage
      get() {
        return current.stage
      }

    /**
     * Concrete target instance / domain configuration.
     * Defines hostnames, base URLs, and other connection details
     * for this specific runtime environment.
     */
    val host: ServiceHost
      get() {
        return current.host
      }

    /**
     * Indicates whether the application/test is running in a Continuous Integration environment.
     * This can affect how resources are managed, tests are executed, and external services are mocked.
     */
    val inCI: Boolean
      get() = executionEnvironment == ExecutionEnvironment.CI

    /**
     * Returns true if the current runtime context is in a development stage.
     */
    val inDev: Boolean
      get() = deploymentStage == DeploymentStage.Development

    val debugMode: Boolean
      get() = current.debugMode

    val inUnitTest: Boolean
      get() = current.inUnitTest


    /**
     * The initial value that is set during application startup.
     */
    private val initialValue = getInitialValue().also {
      require(it.isInitialValue()) {
        "The initial value of the runtime context must have initialValue=true"
      }
    }

    /**
     * The current runtime context.
     *
     * This is set during application startup and must not be modified afterward.
     *
     * Stays at the fallback context until `RuntimeContext.initializeFromEnvironment(serviceHostRegistry)`
     * runs — callers that need to know whether startup already happened ask [isInitialized].
     */
    var current: RuntimeContext<*> = initialValue
      internal set

    /**
     * Returns true if the runtime context has been initialized to a value different from the initial fallback value.
     */
    fun isInitialized(): Boolean {
      return current.isInitialValue().not()
    }

    /**
     * Initializes the current runtime context.
     */
    fun initialize(context: RuntimeContext<*>) {
      this.current = context
    }
  }
}


/**
 * Returns the initial value of the runtime context.
 */
expect fun getInitialValue(): RuntimeContext<ServiceHost.Default>
